import React, { useState, useEffect } from "react";
import GlobalStyles, { CommonClasses } from "../../global/GlobalStyle";
import { useAlert } from "../../contexts/AlertContext";
import { adminAPI } from "../../services/api";
import { User } from "../../types";
import {
  MdMessage,
  MdSearch,
  MdSend,
  MdClear,
  MdRefresh,
  MdInfo,
} from "react-icons/md";

const MessageManagement = () => {
  const { addAlert } = useAlert();
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [message, setMessage] = useState("");

  const MessageIcon = MdMessage as React.FC<React.SVGProps<SVGSVGElement>>;
  const SearchIcon = MdSearch as React.FC<React.SVGProps<SVGSVGElement>>;
  const SendIcon = MdSend as React.FC<React.SVGProps<SVGSVGElement>>;
  const ClearIcon = MdClear as React.FC<React.SVGProps<SVGSVGElement>>;
  const RefreshIcon = MdRefresh as React.FC<React.SVGProps<SVGSVGElement>>;
  const InfoIcon = MdInfo as React.FC<React.SVGProps<SVGSVGElement>>;

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      const response = await adminAPI.getAllUsers();
      setUsers(response.data);
      setFilteredUsers(response.data);
    } catch (error: any) {
      addAlert({ type: "error", message: "Failed to fetch users" });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const filtered = users.filter(
      (user) =>
        user.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.username.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredUsers(filtered);
  }, [searchTerm, users]);

  const handleSendMessage = async (userId: number) => {
    if (!message.trim()) {
      addAlert({ type: "error", message: "Please enter a message" });
      return;
    }

    try {
      await adminAPI.setDisplayMessage({ user_id: userId, message });
      addAlert({ type: "success", message: "Message sent successfully" });
      setMessage("");
      setSelectedUser(null);
      fetchUsers();
    } catch (error: any) {
      addAlert({
        type: "error",
        message: error.response?.data?.error || "Failed to send message",
      });
    }
  };

  const handleClearMessage = async (userId: number) => {
    try {
      await adminAPI.nullifyDisplayMessage({ user_id: userId });
      addAlert({ type: "success", message: "Message cleared successfully" });
      fetchUsers();
    } catch (error: any) {
      addAlert({
        type: "error",
        message: error.response?.data?.error || "Failed to clear message",
      });
    }
  };

  const openMessageModal = (user: User) => {
    setSelectedUser(user);
    setMessage(user.display_message || "");
  };

  const usersWithMessages = users.filter((user) => user.display_message);

  return (
    <div className={GlobalStyles.container}>
      {/* Header */}
      <div
        className={`${CommonClasses.flexBetween} max-sm:flex-col max-sm:items-start max-sm:gap-4`}
      >
        <div>
          <h1
            className={`${CommonClasses.textXl} font-bold ${CommonClasses.textGrayStrong} mb-2`}
          >
            Message Management
          </h1>
          <p className={`${CommonClasses.textGray}`}>
            Send display messages to users and manage existing messages.
          </p>
        </div>
        <button
          onClick={fetchUsers}
          disabled={isLoading}
          className={`${GlobalStyles.buttonIconOutline} ${
            isLoading ? "opacity-50 cursor-not-allowed" : ""
          }`}
        >
          <RefreshIcon
            className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`}
          />
        </button>
      </div>

      {/* Stats and Search */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-3">
          <div className={GlobalStyles.inputWrapper}>
            <SearchIcon className={GlobalStyles.inputIcon} />
            <input
              type="text"
              placeholder="Search users by name, email, or username..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={GlobalStyles.input}
            />
          </div>
        </div>
        <div className={`${GlobalStyles.sectionWrap} text-center`}>
          <div className={`${CommonClasses.textXl} font-bold text-primary`}>
            {usersWithMessages.length}
          </div>
          <div className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}>
            Active Messages
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className={GlobalStyles.tableContainer}>
        {isLoading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className={`${CommonClasses.textGray} mt-4`}>Loading users...</p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="text-center py-8">
            <MessageIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className={`${CommonClasses.textGray} mb-2`}>No users found</p>
            <p className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}>
              {searchTerm
                ? "Try adjusting your search terms"
                : "No users registered yet"}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className={GlobalStyles.table}>
              <thead className={GlobalStyles.tableHeader}>
                <tr>
                  <th className={GlobalStyles.tableHeaderCell}>User</th>
                  <th className={GlobalStyles.tableHeaderCell}>
                    Current Message
                  </th>
                  <th className={GlobalStyles.tableHeaderCell}>Status</th>
                  <th className={GlobalStyles.tableHeaderCell}>Actions</th>
                </tr>
              </thead>
              <tbody className={GlobalStyles.tableBody}>
                {filteredUsers.map((user) => (
                  <tr key={user.id} className={GlobalStyles.tableRow}>
                    <td className={GlobalStyles.tableCell}>
                      <div className={`${CommonClasses.flexStart} gap-3`}>
                        <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                          <span className="text-white font-bold text-sm">
                            {user.first_name[0]}
                            {user.last_name[0]}
                          </span>
                        </div>
                        <div>
                          <div
                            className={`font-medium ${CommonClasses.textGrayStrong}`}
                          >
                            {user.first_name} {user.last_name}
                          </div>
                          <div
                            className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}
                          >
                            @{user.username}
                          </div>
                          <div
                            className={`${CommonClasses.textXs} ${CommonClasses.textGray}`}
                          >
                            {user.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className={GlobalStyles.tableCell}>
                      {user.display_message ? (
                        <div className="max-w-md">
                          <p
                            className={`${CommonClasses.textSm} ${CommonClasses.textGrayStrong} line-clamp-2`}
                          >
                            {user.display_message}
                          </p>
                          <p
                            className={`${CommonClasses.textXs} ${CommonClasses.textGray} mt-1`}
                          >
                            Last updated:{" "}
                            {new Date(user.updated_at).toLocaleString()}
                          </p>
                        </div>
                      ) : (
                        <span className={`${CommonClasses.textGray} italic`}>
                          No message
                        </span>
                      )}
                    </td>
                    <td className={GlobalStyles.tableCell}>
                      {user.display_message ? (
                        <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full dark:bg-green-900 dark:text-green-200">
                          Active
                        </span>
                      ) : (
                        <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded-full dark:bg-gray-900 dark:text-gray-200">
                          No Message
                        </span>
                      )}
                    </td>
                    <td className={GlobalStyles.tableCell}>
                      <div className={`${CommonClasses.flexStart} gap-2`}>
                        <button
                          onClick={() => openMessageModal(user)}
                          className={`${GlobalStyles.buttonIconPrimary}`}
                          title="Send Message"
                        >
                          <SendIcon className="w-4 h-4" />
                        </button>
                        {user.display_message && (
                          <button
                            onClick={() => handleClearMessage(user.id)}
                            className={`${GlobalStyles.buttonIconDanger}`}
                            title="Clear Message"
                          >
                            <ClearIcon className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Send Message Modal */}
      {selectedUser && (
        <div className={GlobalStyles.modalOverlay}>
          <div className={GlobalStyles.modalContainer}>
            <div
              className={`p-5 border-b border-gray-300 dark:border-gray-700 text-center`}
            >
              <h2
                className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong}`}
              >
                Send Message to User
              </h2>
              <p
                className={`${CommonClasses.textSm} ${CommonClasses.textGray} mt-1`}
              >
                This message will be displayed prominently on the user's
                dashboard
              </p>
            </div>

            <div className="p-6 space-y-4">
              {/* User Info */}
              <div className={`${GlobalStyles.sectionWrap}`}>
                <div className={`${CommonClasses.flexStart} gap-3`}>
                  <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
                    <span className="text-white font-bold">
                      {selectedUser.first_name[0]}
                      {selectedUser.last_name[0]}
                    </span>
                  </div>
                  <div>
                    <div
                      className={`font-medium ${CommonClasses.textGrayStrong}`}
                    >
                      {selectedUser.first_name} {selectedUser.last_name}
                    </div>
                    <div
                      className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}
                    >
                      @{selectedUser.username}
                    </div>
                  </div>
                </div>
              </div>

              {/* Current Message */}
              {selectedUser.display_message && (
                <div>
                  <label className={GlobalStyles.label}>Current Message</label>
                  <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                    <p
                      className={`${CommonClasses.textSm} text-yellow-800 dark:text-yellow-200`}
                    >
                      {selectedUser.display_message}
                    </p>
                  </div>
                </div>
              )}

              {/* Message Input */}
              <div className={GlobalStyles.formGroup}>
                <label className={GlobalStyles.label}>
                  {selectedUser.display_message
                    ? "Update Message"
                    : "New Message"}
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Enter your message for the user..."
                  className={`${GlobalStyles.input} min-h-[100px] resize-none`}
                  rows={4}
                />
                <p
                  className={`${CommonClasses.textXs} ${CommonClasses.textGray} mt-1`}
                >
                  This message will be displayed at the top of the user's
                  dashboard
                </p>
              </div>

              {/* Info */}
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <div className={`${CommonClasses.flexStart} gap-3`}>
                  <InfoIcon className="w-5 h-5 text-blue-500 flex-shrink-0" />
                  <div>
                    <p
                      className={`${CommonClasses.textSm} font-medium text-blue-800 dark:text-blue-200`}
                    >
                      Message Guidelines
                    </p>
                    <p
                      className={`${CommonClasses.textXs} text-blue-700 dark:text-blue-300 mt-1`}
                    >
                      Use this for important announcements, account
                      notifications, or support communications.
                    </p>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-4 max-sm:flex-col">
                <button
                  onClick={() => setSelectedUser(null)}
                  className={`${GlobalStyles.buttonOutline} flex-1`}
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleSendMessage(selectedUser.id)}
                  disabled={!message.trim()}
                  className={`${GlobalStyles.buttonPrimary} flex-1 ${
                    !message.trim() ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  <SendIcon className="w-4 h-4 mr-2" />
                  {selectedUser.display_message
                    ? "Update Message"
                    : "Send Message"}
                </button>
                {selectedUser.display_message && (
                  <button
                    onClick={() => handleClearMessage(selectedUser.id)}
                    className={`${GlobalStyles.buttonOutline} max-sm:flex-1`}
                  >
                    <ClearIcon className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MessageManagement;
